import {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useMemo,
} from 'react'

import {
  type OfferDetailsV11,
  type PimProductV13,
} from '@redteclab/api/clients/bully'

import { cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier } from '../model/cartIntermediateAccessorGetRedPointsAmountWithMultiplier'

type CartIntermediateContextValue = {
  currentOffer?: OfferDetailsV11
  errorType?: string
  isAddProductToCartSuccessful: boolean
  product: PimProductV13
  quantity: number
  recommendedOffer?: OfferDetailsV11
  recommendedProduct?: PimProductV13
  redPoints: number
}

const CartIntermediateContext = createContext<
  CartIntermediateContextValue | undefined
>(undefined)

type CartIntermediateContextProviderProps = {
  children: ReactNode
  errorType?: string
  isAddProductToCartSuccessful: boolean
  offer?: OfferDetailsV11
  product: PimProductV13
  quantity: number
  recommendedOffer?: OfferDetailsV11
  recommendedProduct?: PimProductV13
}

export const CartIntermediateContextProvider: FC<
  CartIntermediateContextProviderProps
> = ({
  children,
  errorType,
  isAddProductToCartSuccessful,
  offer,
  product,
  quantity,
  recommendedOffer,
  recommendedProduct,
}) => {
  const offerRedPoints =
    (offer &&
      cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier(
        offer,
      )) ??
    0
  const recommendedOfferRedPoints =
    (recommendedOffer &&
      cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier(
        recommendedOffer,
      )) ??
    0

  const contextValue = useMemo(
    (): CartIntermediateContextValue => ({
      currentOffer: offer,
      errorType,
      isAddProductToCartSuccessful,
      product,
      quantity,
      recommendedOffer,
      recommendedProduct,
      redPoints: offerRedPoints + recommendedOfferRedPoints,
    }),
    [
      offer,
      product,
      quantity,
      recommendedOffer,
      recommendedProduct,
      offerRedPoints,
      recommendedOfferRedPoints,
      isAddProductToCartSuccessful,
      errorType,
    ],
  )

  return (
    <CartIntermediateContext.Provider value={contextValue}>
      {children}
    </CartIntermediateContext.Provider>
  )
}

export const useCartIntermediateContext = (): CartIntermediateContextValue => {
  const contextValue = useContext(CartIntermediateContext)
  if (contextValue === undefined) {
    throw new Error(
      'useProductPageContext must be used within a ProductPageContextProvider',
    )
  }

  return contextValue
}
